import { SocialLinks, ReviewsStrip } from "@/components/SocialLinks";
import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Mail, MapPin, Phone, Check } from "lucide-react";
import { SERVICES } from "@/data/services";
import GoogleReviewCta from "@/components/GoogleReviewCta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", required, value, onChange, ...rest }) => (
  <label className="block">
    <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-2">
      {label}{required && " *"}
    </div>
    {type === "textarea" ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={6}
        className="w-full bg-transparent border-b border-black/15 py-3 text-[16px] text-ink placeholder:text-muted/50 focus:outline-none focus:border-brand transition-colors resize-none"
        data-testid={`contact-field-${name}`}
        {...rest}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-black/15 py-3 text-[16px] text-ink placeholder:text-muted/50 focus:outline-none focus:border-brand transition-colors"
        data-testid={`contact-field-${name}`}
        {...rest}
      />
    )}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    service_interest: "",
    message: "",
    website: "", // honeypot
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({
        first_name: "", last_name: "", email: "", phone: "", company: "",
        role: "", service_interest: "", message: "", website: "",
      });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail?.[0]?.msg || err?.message || "Something went wrong");
    }
  };

  return (
    <>
      <section className="pt-[160px] pb-12 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6">
          [ Contact · Calmbreeze ]
        </div>
        <h1 className="font-display font-bold text-[3rem] md:text-[5rem] leading-[0.98] tracking-tightest max-w-5xl">
          Let’s talk about
          <span className="text-brand"> your air.</span>
        </h1>
        <p className="mt-8 text-[18px] text-muted-foreground max-w-2xl leading-relaxed">
          Whether you need a TR19 clean, a fire damper survey, or a full HVAC compliance partner —
          drop us a line and we’ll come back the same working day.
        </p>
      </section>

      <section className="py-16 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-3">[ Office ]</div>
              <div className="font-display text-[1rem] text-ink leading-relaxed">
                14 Hyde Lodge,<br />
                11 Pennyroyal Drive,<br />
                West Drayton, UB7 9FF
              </div>
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-3">[ Phone ]</div>
              <a
                href="tel:+441895919333"
                className="font-display text-[1.4rem] tracking-tight text-ink hover:text-brand inline-flex items-center gap-2"
                data-testid="contact-phone-link"
              >
                <Phone className="h-5 w-5 text-brand" /> 01895 919 333
              </a>
              <div className="mt-2 text-[13px] text-muted-foreground">
                Or message us on <a href="https://wa.me/447881521243" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline" data-testid="contact-whatsapp-link">WhatsApp</a>.
              </div>
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-3">[ Email ]</div>
              <a
                href="mailto:Hello@calmbreeze.co.uk"
                className="font-display text-[1.4rem] tracking-tight text-ink hover:text-brand inline-flex items-center gap-2"
                data-testid="contact-email-link"
              >
                <Mail className="h-5 w-5 text-brand" /> Hello@calmbreeze.co.uk
              </a>
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-3">[ Location ]</div>
              <div className="text-[16px] text-ink/80 inline-flex items-start gap-2">
                <MapPin className="h-5 w-5 text-brand mt-0.5" /> West London,<br />United Kingdom
              </div>
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-3">[ Find us online ]</div>
              <SocialLinks size="lg" testIdPrefix="contact-social" />
              <div className="mt-3"><ReviewsStrip testId="contact-reviews" /></div>
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-3">[ Hours ]</div>
              <div className="text-[14px] text-muted-foreground leading-relaxed">
                Monday — Friday<br />07:30 — 18:00<br />After-hours work scheduled by arrangement.
              </div>
            </div>
            <div className="pt-8 border-t border-black/8">
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-3">[ What happens next ]</div>
              <ul className="space-y-3 text-[13px] text-muted-foreground">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand mt-0.5" /> We’ll reply the same working day.</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand mt-0.5" /> A short discovery call to scope your site.</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand mt-0.5" /> A clear, fixed quote — no surprises.</li>
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {status === "success" ? (
              <div
                className="bg-paper-warm border border-black/5 rounded-md p-12"
                data-testid="contact-success"
              >
                <div className="font-mono text-[11px] tracking-overline uppercase text-brand mb-4">[ Sent ]</div>
                <h2 className="font-display font-bold text-[2rem] md:text-[2.5rem] tracking-tightest leading-tight mb-4">
                  Thank you — we’ve got your message.
                </h2>
                <p className="text-[16px] text-muted-foreground max-w-md leading-relaxed mb-8">
                  A member of our team will be in touch the same working day. If it’s urgent,
                  you can also email us directly.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  data-testid="contact-send-another"
                  className="inline-flex items-center gap-2 text-[14px] font-semibold border-b border-ink/30 pb-1 hover:border-ink transition-colors"
                >
                  Send another message <ArrowUpRight className="h-4 w-4" />
                </button>
                <div className="mt-10 pt-8 border-t border-black/10">
                  <GoogleReviewCta variant="inline" />
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="First name" name="first_name" required value={form.first_name} onChange={onChange} />
                  <Field label="Last name" name="last_name" required value={form.last_name} onChange={onChange} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} />
                  <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} />
                </div>
                <Field label="Company / Organisation (optional)" name="company" value={form.company} onChange={onChange} />

                <label className="block">
                  <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-2">
                    I am a… <span className="text-accent">*</span>
                  </div>
                  <select
                    name="role"
                    required
                    value={form.role}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-black/15 py-3 text-[16px] text-ink focus:outline-none focus:border-brand transition-colors appearance-none"
                    data-testid="contact-field-role"
                  >
                    <option value="">— Select one —</option>
                    <option value="Facilities Manager">Facilities / Property Manager</option>
                    <option value="Business Owner">Business Owner</option>
                    <option value="School">School / Academy</option>
                    <option value="FM Contractor">FM Contractor</option>
                    <option value="Supplier">Supplier / Vendor</option>
                    <option value="Other">Other</option>
                  </select>
                </label>

                <label className="block">
                  <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-2">
                    Service interest (optional)
                  </div>
                  <select
                    name="service_interest"
                    value={form.service_interest}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-black/15 py-3 text-[16px] text-ink focus:outline-none focus:border-brand transition-colors appearance-none"
                    data-testid="contact-field-service_interest"
                  >
                    <option value="">— Select a service —</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.title}>{s.title}</option>
                    ))}
                    <option value="General enquiry">General enquiry</option>
                  </select>
                </label>

                <Field label="How can we help?" name="message" type="textarea" required value={form.message} onChange={onChange} placeholder="Tell us about your site, scope, timeline…" />

                {/* honeypot - hidden from real users */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                  className="absolute -left-[9999px] -top-[9999px]"
                  aria-hidden="true"
                />

                {status === "error" && (
                  <div className="text-[13px] text-accent font-mono" data-testid="contact-error">
                    {errorMsg || "Something went wrong. Please try again."}
                  </div>
                )}

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 px-7 py-4 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "sending" ? "Sending…" : "Send message"}
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
